import { serviceChoices } from "./manage/consent-model.js";
import { api, ApiRequestError, type FrameSummary, type PageLoad, type PublishPiece, type ReferenceMoves, type UploadRename, type User } from "./api.js";
import { initAssetsPanel } from "./assets-panel.js";
import { initAutoBem } from "./auto-bem.js";
import { initCanvas } from "./canvas.js";
import { initCanvasSize } from "./canvas-size.js";
import { initCodePanel } from "./code-panel/index.js";
import { initComponentMode, type ComponentMode } from "./component-mode.js";
import { initComponentsDialog, type ComponentsDialog } from "./components-dialog.js";
import { initConfirm } from "./confirm.js";
import { initConflictDialog, pageConflict } from "./conflict.js";
import { initFormDialog } from "./form-dialog.js";
import { createIcon, drawIcons } from "./icons.js";
import { initTooltips, refused, setLabel, setOff, setTip } from "./tooltip.js";
import { initContentFields } from "./content-fields.js";
import { initContentPanel } from "./content-panel/index.js";
import { initEntryChip } from "./entry-chip.js";
import { entryIn, readEntryChoice } from "./entry-choice.js";
import { initElementPanel, type ElementPanelCommands } from "./element-panel/index.js";
import { initHistoryBar, type HistoryCommands } from "./history-bar.js";
import { createElement, query } from "./dom.js";
import { initManage, type ManageGroup, type ManageSection } from "./manage/index.js";
import { initAccountSection } from "./manage/account-section.js";
import { initPreferencesSection } from "./manage/preferences-section.js";
import { initAddressSection } from "./manage/address-section.js";
import { initCommentsSection } from "./manage/comments-section.js";
import { initBeanSection } from "./manage/beancss-section.js";
import { initCanvasScheme, setsColorScheme, type CanvasScheme } from "./canvas-scheme.js";
import { initFontsSection } from "./manage/fonts-section.js";
import { initFormsSection } from "./manage/forms-section.js";
import { commentsWaiting, messagesWaiting, waitingComments } from "./manage/waiting.js";
import { initComponentsSection } from "./manage/components-section.js";
import { initMembers } from "./manage/members-section.js";
import { initPagesSection, type PagesSection } from "./manage/pages-section.js";
import { initPeople } from "./manage/people-section.js";
import { initRedirectsSection } from "./manage/redirects-section.js";
import { initRoutesSection } from "./manage/routes-section.js";
import { initFramesSection, type FramesSection } from "./manage/frames-section.js";
import { initFrameMode, type FrameMode } from "./frame-mode.js";
import { initConsentMode, type ConsentMode } from "./consent-mode.js";
import { initConsentSection } from "./manage/consent-section.js";
import { initConsentsSection } from "./manage/consents-section.js";
import { applySiteClock, initWebsiteSection, type WebsiteSection } from "./manage/website-section.js";
import { initPages, pageUrl, templateCollection, type Session } from "./pages.js";
import { initPublish, type PublishHooks } from "./publish.js";
import type { PublishAction, PublishTarget } from "./publish-model.js";
import { createPublishCard } from "./publish-card.js";
import { initImagePicker } from "./image-picker.js";
import { initLightbox } from "./lightbox.js";
import { initResizer } from "./resizer.js";
import { Settings } from "./settings.js";
import { initSidebar } from "./sidebar.js";
import { initStructurePanel, type StructureCommands } from "./structure-panel/index.js";
import { initStyleManager } from "./style-manager/index.js";
import { usageElsewhere } from "./style-manager/selectors.js";
import { initTaskbar } from "./taskbar.js";
import { initWidgetsDialog } from "./widgets-dialog.js";
import { initConsentWidgetsDialog } from "./consent-widgets-dialog.js";
import { showError, showToast, type ToastHandle } from "./toast.js";
import { openTab } from "./new-tab.js";
import { draftRefusal, roleOf } from "./page-roles.js";
import { onMaintenanceChange } from "./manage/maintenance.js";
import { initMaintenanceCard } from "./manage/maintenance-card.js";
import { initNoticeCard } from "./manage/notice-card.js";
import { initSectionTabs, type SectionTabs } from "./tabs.js";
import { copyText } from "./clipboard.js";
import { HiddenElements } from "./tree/hidden.js";
import { setPresets } from "./tree/presets.js";
import { PageStore, pageFingerprint, stylesFingerprint } from "./tree/store.js";
import { createDebounce } from "./debounce.js";
import { handOverComponent, takeHandedComponent } from "./component-handoff.js";
import { createPageSaver, saveButtonView, STYLES_TIP, type SaveTarget } from "./page-saver.js";
import { createStylesAutosave, stylesStatusText, type StylesSaveState } from "./styles-autosave.js";
import { initStyleVersions } from "./style-versions.js";
import { initHistoryDialog } from "./history-dialog.js";
import { dateTimeText } from "./dates.js";
import type { Move } from "./tree/references.js";
import { StyleStore } from "./tree/styles.js";

const app = query("[data-app]");
drawIcons(document);
/** How often the waiting comments are asked for while the builder is open. */
const COMMENTS_EVERY = 3 * 60 * 1000;

// A promise that fails outside any handler would otherwise vanish into the console.
window.addEventListener("unhandledrejection", (event) => {
  const reason: unknown = event.reason;
  showError(reason);
});

initResizer({
  handle: query('[data-resizer="editor"]', app),
  container: app,
  property: "--app-editor-height",
  edge: "bottom",
  initial: 260,
  min: 120,
  max: () => app.clientHeight - 220,
  storageKey: "elpresso.barista.editor-height",
});

initResizer({
  handle: query('[data-resizer="sidebar"]', app),
  container: app,
  property: "--app-sidebar-width",
  edge: () => (app.dataset["sidebarAt"] === "start" ? "left" : "right"),
  initial: 280,
  min: 220,
  max: () => app.clientWidth - 480,
  storageKey: "elpresso.barista.sidebar-width",
});

initCanvasSize(query("[data-canvas-area]", app), app);
const sidebar = initSidebar(query("[data-sidebar]", app), app);

// ---- Which page ----

/**
 * This instance serves one website, so the URL only names the page: /pages/<page>.
 * Without one the first page is opened, which is what the login and the home link do.
 */
async function resolveRoute(): Promise<string> {
  const match = /^\/pages\/([^/]+)$/.exec(location.pathname);
  if (match) return decodeURIComponent(match[1]!);
  const page = (await api.site()).pages[0];
  if (!page) throw new Error("The website has no page");
  // window.history, not the undo history this module names `history` further down.
  window.history.replaceState(null, "", pageUrl(page.id));
  return page.id;
}

let session: Session;
let loaded: PageLoad;
/** The owner built this website; an editor maintains what a visitor reads. */
let role: "owner" | "editor" = "owner";
/** The logged-in account; the people section hides the row that would remove it. */
let account: User;
try {
  // The website's clock and format come before anything writes a date: every list reads them.
  const [presets, me, site] = await Promise.all([api.presets(), api.me(), api.site().catch(() => null)]);
  if (site) applySiteClock(site.site);
  setPresets(presets);
  role = me.role;
  account = me;
  const pageId = await resolveRoute();
  loaded = await api.page(pageId);
  // Read before the canvas renders, so it never shows another entry first.
  const choice = readEntryChoice(pageId);
  const collection = templateCollection(loaded.collections, choice?.collection ?? "");
  session = {
    pageId,
    serves: loaded.collections,
    collection,
    entry: entryIn(choice, collection)?.slug ?? "",
    pageRevision: loaded.pageRevision,
    siteRevision: loaded.siteRevision,
    stylesHash: loaded.stylesHash,
  };
} catch (error) {
  if (error instanceof ApiRequestError && error.unauthorized) {
    location.replace("/login");
    throw error;
  }
  query("[data-structure-empty]", app).textContent =
    `The page could not be loaded: ${error instanceof Error ? error.message : String(error)}`;
  app.classList.remove("app--starting");
  throw error;
}

const store = new PageStore(loaded.document);
store.mode = role === "owner" ? "full" : "content";
const styles = new StyleStore(store);
const settings = new Settings();
const hidden = new HiddenElements(store);

initConfirm(query<HTMLDialogElement>("[data-confirm]"));
initFormDialog(query<HTMLDialogElement>("[data-form-dialog]"));
initTooltips();

/**
 * What an editor gets. Everything that shapes a page — the elements to insert, the
 * structure tree, the element settings, the code editors, the style manager — belongs to
 * the owner, so it is not wired at all; in its place the sidebar shows the page's texts
 * and pictures as fields. The store refuses structural changes on top of that, and the
 * core refuses them again on save, so this is convenience rather than the boundary.
 */
const editorOnly = role !== "owner";
let history: HistoryCommands;
let structure: StructureCommands;
let element: ElementPanelCommands;
/** The owner's: making, editing and detaching components. Wired once Save exists, below. */
let componentMode: ComponentMode | null = null;
let frameMode: FrameMode | null = null;
let consentMode: ConsentMode | null = null;
/** The taskbar's opener of the consent widgets, which only the consent mode shows. */
let consentWidgetsOpener: HTMLButtonElement | null = null;
/** The frames section of the manage panel, for the owner; the switcher opens a frame's settings there. */
let framesSection: FramesSection | null = null;
/** The pages and templates sections, for the owner; the switcher opens a page's settings there. */
let pagesSection: PagesSection | null = null;
/** Website → Settings, whose tab General the setup leads to. */
let websiteSection: WebsiteSection | null = null;
/** The tabs of the Pages section, Pages and Settings: a page opened from elsewhere shows on the first. */
let pagesTabs: SectionTabs | null = null;
let templatesSection: PagesSection | null = null;
let componentsDialog: ComponentsDialog | null = null;
/**
 * How many entries of a collection "Show only when" suggests placeholders of, the first in
 * the collection's order; any other entry can still be typed.
 */
const CONDITION_ENTRY_LIMIT = 200;
if (editorOnly) {
  for (const name of ["structure", "element"]) {
    query(`[data-sidebar-section="${name}"]`, app).hidden = true;
  }
  query('[data-sidebar-section="content-fields"]', app).hidden = false;
  query('[data-resizer="element-panel"]', app).hidden = true;
  query("[data-code-panel]", app).hidden = true;
  query('[data-resizer="editor"]', app).hidden = true;
  app.style.setProperty("--app-editor-height", "0px");
  // Preview, export and publish are the owner's for now, and the core refuses them
  // anyway; an editor saves, and the owner decides when that reaches the website.
  for (const hook of ["[data-taskbar-elements]", "[data-publish]"]) {
    query(hook, app).hidden = true;
  }
  // The manage panel is built further down; the button is only pressed after that.
  initContentFields(query("[data-content-fields]", app), store, { openEntries: () => manage.show("content"), imagePicker: () => imagePicker });
  history = initHistoryBar(query("[data-taskbar]", app), store, styles);
  // The canvas asks for these on a keyboard shortcut or a double click. An editor has no
  // tree and no element settings, so they do nothing rather than reaching into a panel
  // that is not there.
  structure = { duplicate: () => {}, rename: () => {}, move: () => {}, collapseAll: () => {}, previewInsert: () => {} };
  element = { addAttribute: () => {}, showCondition: () => {}, contentChanged: () => {}, focusContent: () => false };
} else {
  // The tree is built right below; the bar only marks it once somebody points at a button.
  const openers = initTaskbar(query("[data-taskbar-elements]", app), store, {
    preview: (target) => structure.previewInsert(target),
    // A heading or a text is inserted to be written: its Content takes the focus.
    inserted: () => element.focusContent(),
  });
  initWidgetsDialog(query<HTMLDialogElement>("[data-widgets]"), openers.widgets, store);
  // The consent mode, declared further down, has a dialog and an opener of its own (#96).
  consentWidgetsOpener = openers.consentWidgets;
  initConsentWidgetsDialog(query<HTMLDialogElement>("[data-consent-widgets]"), openers.consentWidgets, {
    list: () => consentMode?.widgets.list() ?? null,
    off: (id) => consentMode?.widgets.off(id) ?? null,
    insert: (id) => consentMode?.widgets.insert(id),
  });
  componentsDialog = initComponentsDialog(
    query<HTMLDialogElement>("[data-components]"),
    query<HTMLDialogElement>("[data-component-name-dialog]"),
    openers.components,
    store,
  );
  history = initHistoryBar(query("[data-taskbar]", app), store, styles, {
    // Undoing a convert leaves the new component stored; the undo toast offers to delete it.
    deleteComponent: (component) =>
      void api.deleteComponent(component.id).then(
        () => {
          store.dropComponentFromHistory(component.id);
          store.forgetComponent(component.id);
          showToast(`Deleted ${component.name}`, { kind: "success" });
        },
        (error: unknown) => showError(error, `${component.name} was not deleted`),
      ),
  });
  const tree = initStructurePanel(query("[data-structure]", app), store, {
    hidden,
    autoBem: initAutoBem(query<HTMLDialogElement>("[data-auto-bem]"), store),
    menu: query("[data-context-menu]"),
    components: {
      convert: (id) => componentMode?.commands.convert(id),
      edit: (id) => componentMode?.commands.edit(id),
      detach: (id) => componentMode?.commands.detach(id),
      exit: () => componentMode?.commands.exit(),
    },
    // Declared right below; only called from the menu, after both exist.
    showCondition: () => element.showCondition(),
    focusContent: () => element.focusContent(),
  });
  structure = tree;
  query("[data-structure-collapse-all]", app).addEventListener("click", () => tree.collapseAll());
  element = initElementPanel(
    query("[data-element-panel]", app),
    store,
    sidebar,
    api.collections,
    (collectionId) => api.entries(collectionId, { limit: CONDITION_ENTRY_LIMIT }).then((page) => page.entries),
    {
      edit: (id) => componentMode?.commands.edit(id),
      detach: (id) => componentMode?.commands.detach(id),
    },
    session.pageId,
    async () => (await api.forms()).forms,
    {
      imagePicker: () => (editorOnly ? null : imagePicker),
      services: async () => (editorOnly ? [] : serviceChoices(await api.consent())),
    },
  );
  initCodePanel(query("[data-code-panel]", app), store, styles, settings, {
    loadUsage: async () => usageElsewhere(await api.classUsage(), session.pageId),
    services: async () => serviceChoices(await api.consent()),
  });
}

// The role decided what is there: the builder shows itself, the owner's tools only to the
// owner. Before this, nothing showed, so an editor never sees them flash up.
app.classList.remove("app--starting");

// ---- Dirty state and saving ----

/**
 * The page and the site styles as they were loaded or last saved. Unsaved means different
 * from them, not "touched since": undoing back to the saved state leaves nothing to lose.
 * The two are apart because a component save stores the styles without the page, and the
 * components not at all, since they are saved on their own.
 */
let savedPage = pageFingerprint(store.toJSON());
let savedStyles = stylesFingerprint(store.toJSON());
/** Whether anything but the selection changed since then; spares the comparison until it did. */
let touched = false;
/** Set once the page is left on purpose, so the browser does not ask a second time. */
let leaving = false;
/** The page itself — its tree, ids, scripts and meta — differs from the stored one. */
const pageDirty = () => touched && pageFingerprint(store.toJSON()) !== savedPage;
/** The site styles differ from the stored ones; they save themselves, see stylesAutosave. */
const stylesDirty = () => touched && stylesFingerprint(store.toJSON()) !== savedStyles;
/**
 * Whether the stored styles wait for Save to go live, as the core said last, and whether the
 * website was published at all, before which nothing goes live (decided 2026-11-03): the
 * autosave stores the styles, Save puts them on the website.
 */
let stylesWaiting = false;
let websitePublished = false;
/** Whether Save puts styles on the website: stored ones that wait, or ones not even stored yet. */
const stylesToGo = (): boolean => !editorOnly && (stylesWaiting || (websitePublished && stylesDirty()));
/** The page, the site styles or a component being edited holds something that is not saved. */
const isDirty = () => pageDirty() || stylesDirty() || (componentMode?.isDirty() ?? false) || (frameMode?.isDirty() ?? false) || (consentMode?.isDirty() ?? false);

/**
 * Runs the saves that send the site styles one after the other: the styles' own, the
 * page's and a component's all carry the revision they loaded, and two of them on the way
 * at once would make the second look stale to the core.
 */
let saveQueue: Promise<unknown> = Promise.resolve();
const exclusive = <T>(save: () => Promise<T>): Promise<T> => {
  const run = saveQueue.then(save, save);
  saveQueue = run.catch(() => undefined);
  return run;
};

/** The toast of the last refused styles save, closed once a later save gets through. */
let stylesRefusal: ToastHandle | null = null;
/**
 * The site styles save themselves a moment after they changed — in the style manager, the
 * CSS editor, by a rename or an undo — so nothing is lost, and go live with Save, which sends
 * them with live (decided 2026-11-03): the canvas and the preview show them at once, visitors
 * once somebody says so.
 */
const stylesAutosave = createStylesAutosave({
  dirty: stylesDirty,
  blocked: () => styles.siteCssProblems().length > 0,
  save: (live) =>
    exclusive(async () => {
      const sent = store.toJSON();
      try {
        const result = await api.saveStyles({
          styles: { classes: sent.classes, elements: sent.elements, stylesheets: sent.stylesheets, base: sent.base },
          siteRevision: session.siteRevision,
          stylesHash: session.stylesHash,
          ...(live ? { live } : {}),
        });
        session.siteRevision = result.siteRevision;
        session.stylesHash = result.stylesHash;
        stylesWaiting = result.waiting;
        websitePublished = result.website;
        if (result.styles) {
          // Another page changed the site styles meanwhile and this copy was the one it
          // loaded: the builder takes the stored ones.
          styles.replaceSiteStyles(result.styles);
          savedStyles = stylesFingerprint(store.toJSON());
        } else {
          savedStyles = stylesFingerprint(sent);
        }
        stylesRefusal?.close();
        stylesRefusal = null;
        return true;
      } catch (error) {
        // An unanswered save is tried again by itself; the status says so, a toast every
        // few seconds while the server restarts would not.
        if (error instanceof ApiRequestError && error.unreachable) return "unreachable";
        stylesRefusal = showError(error, "The styles were not saved");
        return false;
      }
    }),
});
/**
 * Saves the styles before a save somebody asked for, and says so when CSS the core refuses
 * holds them back, since the autosave only shows that in the style status.
 */
const flushStylesForSave = async (): Promise<boolean> => {
  const ok = await stylesAutosave.flush();
  if (!ok && stylesAutosave.state === "blocked") showToast("The styles were not saved", { kind: "error", detail: styles.siteCssProblems()[0] });
  return ok;
};
// Back online: an unanswered save need not wait for its next try.
window.addEventListener("online", () => {
  if (stylesAutosave.state === "waiting") void stylesAutosave.flush();
});
/**
 * A collection or entry got another slug and the core rewrote the pages and components
 * naming it. The open page follows the same way and takes the new revision when it had
 * the one before, so its next save is no conflict; a page that was saved stays saved.
 */
const followMovedReferences = (move: Move, moved: ReferenceMoves): void => {
  const clean = !pageDirty();
  store.moveReferences(move);
  store.adoptComponentRevisions(moved.components);
  const step = moved.pages[session.pageId];
  if (step && step.before === session.pageRevision) {
    session.pageRevision = step.after;
    if (clean) savedPage = pageFingerprint(store.toJSON());
  }
};
/**
 * An upload got another name, and the core rewrote the pages, components, frames, styles
 * and entries naming it (#104). The open page follows the same way and takes the new
 * revisions, so its next save and the next styles save are no conflict; what was saved
 * stays saved.
 */
const followRenamedUpload = (result: UploadRename): void => {
  const clean = !pageDirty();
  const stylesClean = savedStyles === stylesFingerprint(store.toJSON());
  store.renameUpload(result.from, result.to);
  store.adoptComponentRevisions(result.components);
  session.siteRevision = result.siteRevision;
  session.stylesHash = result.stylesHash;
  if (stylesClean) savedStyles = stylesFingerprint(store.toJSON());
  const step = result.revisions[session.pageId];
  if (step && step.before === session.pageRevision) {
    session.pageRevision = step.after;
    if (clean) savedPage = pageFingerprint(store.toJSON());
  }
};
/**
 * Says where the styles stand, in the style manager always, in the taskbar only while they
 * travel or failed and then briefly once they arrived or went live, so it is quiet most of the
 * time; the style manager's Save is on while they wait to go live. An editor's builder has no
 * style manager in the document at all.
 */
const showStylesState = (() => {
  const managerStatus = app.querySelector<HTMLElement>('[data-styles-status="manager"]');
  const taskbarStatus = app.querySelector<HTMLElement>('[data-styles-status="taskbar"]');
  const liveButton = app.querySelector<HTMLButtonElement>("[data-styles-live]");
  const quiet = createDebounce(2000);
  let shown: StylesSaveState = stylesAutosave.state;
  return (speak = false): void => {
    const state = stylesAutosave.state;
    const failed = state === "failed" || state === "waiting" || state === "blocked";
    const live = { website: websitePublished, waiting: stylesWaiting };
    if (managerStatus) {
      managerStatus.textContent = stylesStatusText(state, "manager", live);
      managerStatus.classList.toggle("style-manager__saved--failed", failed);
      managerStatus.classList.toggle("style-manager__saved--waiting", state === "saved" && live.website && live.waiting);
    }
    if (liveButton) {
      const going = state === "going-live";
      liveButton.classList.toggle("button--primary", stylesToGo());
      setOff(liveButton, going ? "Going live" : stylesToGo() ? null : websitePublished ? "The styles are live" : "Nothing is on the website yet: the styles go with the first publish", STYLES_TIP);
    }
    if (taskbarStatus && (speak || state !== shown)) {
      taskbarStatus.textContent = stylesStatusText(state, "taskbar", live);
      taskbarStatus.classList.toggle("taskbar__styles--failed", failed);
      quiet.cancel();
      if (state === "saved") quiet.schedule(() => (taskbarStatus.textContent = ""));
    }
    shown = state;
  };
})();
stylesAutosave.onState(() => {
  showStylesState();
  saveStateSoon.schedule(showSaveState);
});
if (!editorOnly) {
  // Whether saved styles wait from before, and whether the website was published at all.
  void api
    .site()
    .then((summary) => {
      stylesWaiting = summary.stylesWaiting;
      websitePublished = Date.parse(summary.publishedAt) > 0;
      showStylesState();
      showSaveState();
    })
    .catch(() => undefined);
}
{
  // The style manager's own Save and the history of the styles; the owner's alone.
  const liveButton = app.querySelector<HTMLButtonElement>("[data-styles-live]");
  liveButton?.addEventListener("click", () => {
    if (refused(liveButton)) return;
    void stylesGoLive("The styles are live");
  });
  const historyButton = app.querySelector<HTMLButtonElement>("[data-styles-history]");
  if (historyButton) {
    setLabel(historyButton, "Styles history: every version that went live");
    const versions = initStyleVersions(query<HTMLDialogElement>("[data-style-versions]"), {
      restore: async (version) => {
        // What waits is stored first, so the restore replaces nothing unsaved.
        if (!(await stylesAutosave.flush())) return false;
        try {
          const result = await exclusive(() => api.restoreStyleVersion(version.id));
          session.siteRevision = result.siteRevision;
          session.stylesHash = result.stylesHash;
          styles.replaceSiteStyles(result.styles);
          savedStyles = stylesFingerprint(store.toJSON());
          stylesWaiting = result.waiting;
          websitePublished = result.website;
          showStylesState();
          showSaveState();
          showToast(`The styles of ${dateTimeText(version.at)} are live again`, { kind: "success" });
          return true;
        } catch (error) {
          showError(error, "The styles were not restored");
          return false;
        }
      },
    });
    historyButton.addEventListener("click", () => void versions.open());
  }
}
/**
 * Puts the styles on the website, as Save does when they wait: the autosave sends them with
 * live. Says so with the words given, or why not.
 */
const stylesGoLive = async (said: string | null): Promise<boolean> => {
  const ok = await stylesAutosave.goLive();
  if (!ok && stylesAutosave.state === "blocked") showToast("The styles did not go live", { kind: "error", detail: styles.siteCssProblems()[0] });
  else if (ok && said !== null) showToast(websitePublished ? said : "The styles are saved", { kind: "success" });
  showStylesState(true);
  showSaveState();
  return ok;
};
store.onChange((origin) => {
  if (origin === "selection") return;
  touched = true;
  if (!editorOnly) stylesAutosave.changed();
});
styles.onChange(() => {
  touched = true;
  if (!editorOnly) stylesAutosave.changed();
});
// A CSS edit records a step without a change of the store, so the commit is listened to too.
store.onCommit(() => {
  if (!editorOnly) stylesAutosave.changed();
});
window.addEventListener("beforeunload", (event) => {
  if (!leaving && isDirty()) {
    event.preventDefault();
    // Chrome before 119 asks only when returnValue is set as well.
    event.returnValue = "";
  }
});

// The whole history of a piece, which every Publishing card opens.
initHistoryDialog(query<HTMLDialogElement>("[data-history-dialog]"));

/** Picks and uploads the website's pictures, for image fields and share pictures alike. */
const imagePicker = initImagePicker(query<HTMLDialogElement>("[data-image-picker]"));

/** Shows an upload large, over the whole window. */
const lightbox = initLightbox(query<HTMLDialogElement>("[data-lightbox]"));

const pages = initPages(query("[data-pages]", app), query<HTMLTemplateElement>("[data-page-form-template]"), session, store, {
  isDirty,
  navigate: (url) => {
    // The switcher asked already; the browser must not ask again.
    leaving = true;
    location.assign(url);
  },
  // Declared further down; only called once somebody leaves the page. Leaving means both
  // the component being edited and the page itself are kept.
  save: () => saveEverything(),
  // The styles save themselves, so waiting ones go before anybody is asked about the page.
  settle: () => (editorOnly ? Promise.resolve(true) : stylesAutosave.flush()),
  adoptStored: (change) => {
    const clean = !pageDirty();
    change();
    if (clean) savedPage = pageFingerprint(store.toJSON());
  },
  // The page settings are the owner's; the core refuses an editor's change anyway.
  settings: !editorOnly,
  imagePicker: editorOnly ? null : imagePicker,
  // Declared further down; only called once somebody publishes from a page's settings.
  ...(editorOnly
    ? {}
    : {
        publish: (action: PublishAction, target: PublishTarget, hooks: PublishHooks) => publish.act(action, target, hooks),
        takeOff: (items: PublishPiece[]) => publish.takeOff(items),
      }),
  // In the inspector of the section that lists the page; the manage panel is declared below.
  openSettings: (page) => {
    (page.template ? templatesSection : pagesSection)?.openPage(page.id);
    if (!page.template) pagesTabs?.show("pages");
    manage.show(page.template ? "templates" : "pages");
  },
  openNew: (role) => {
    pagesSection?.openNew(role);
    pagesTabs?.show("pages");
    manage.show("pages");
  },
  // Frames are the owner's: the switcher lists them and edits the one picked.
  ...(editorOnly
    ? {}
    : {
        frames: {
          edit: (frame: FrameSummary) => void editFrame(frame),
          leave: () => void frameMode?.leave(),
          // In the inspector of the Frames section, as a page's settings are in the Pages one.
          settings: (frame: FrameSummary) => {
            void framesSection?.openSettings(frame.id);
            manage.show("frames");
          },
        },
        // The elements of the cookie consent, opened in the consent mode from the switcher.
        consent: {
          list: async () => (await api.consent()).items.map((item) => ({ id: item.id, name: item.name, kind: item.kind, active: item.active, designed: item.root !== null })),
          edit: (itemId: string) => void editConsent(itemId),
          settings: () => manage.show("consent"),
        },
      }),
});

const saveButton = query<HTMLButtonElement>("[data-save]", app);

/** What Save saves now: the component or the frame being edited, else the page. */
const saveTarget = (): SaveTarget => {
  const component = store.editedComponent;
  if (component !== null) return { kind: "component", name: component.name };
  const frame = store.editedFrame;
  if (frame !== null) return { kind: "frame", name: frame.name };
  if (store.editedConsent !== null) return { kind: "consent", name: store.editedConsent.name };
  return { kind: "page" };
};
/**
 * Whether what Save saves has changes that are not stored; the styles store themselves, and
 * Save puts them live where they wait (stylesToGo), which counts as well.
 */
const saveDirty = (): boolean => saveOwnDirty() || stylesToGo();
/** Whether the page, or what a mode edits, has changes that are not stored. */
const saveOwnDirty = (): boolean => {
  if (componentMode?.active) return componentMode.isDirty();
  if (frameMode?.active) return frameMode.isDirty();
  if (consentMode?.active) return consentMode.isDirty();
  return pageDirty();
};
/**
 * Shows on Save, in the tab's title and in the page switcher whether something waits for
 * Save, so nobody has to press Ctrl+S to find out (#69). The modes set Save's colour, this
 * its words and its state.
 */
/** Draws the Publish button anew; set once the release end of the taskbar exists, below. */
let refreshPublish = (): void => undefined;
const showSaveState = (): void => {
  refreshPublish();
  const view = saveButtonView(saveTarget(), saveOwnDirty(), editorOnly, stylesToGo());
  const words = document.createTextNode(view.text);
  if (view.unsaved) saveButton.replaceChildren(createElement("span", "taskbar__unsaved"), words);
  else saveButton.replaceChildren(words);
  saveButton.setAttribute("aria-label", view.label);
  setOff(saveButton, view.off ? view.tip : null, view.tip);
  pages.setUnsaved(pageDirty() || (componentMode?.isDirty() ?? false) || (frameMode?.isDirty() ?? false));
};
// Comparing the page with the stored one costs a walk of the tree, so typing is not
// followed keystroke by keystroke.
const saveStateSoon = createDebounce(150);
store.onChange((origin) => {
  if (origin !== "selection") saveStateSoon.schedule(showSaveState);
});
store.onCommit(() => saveStateSoon.schedule(showSaveState));
styles.onChange(() => saveStateSoon.schedule(showSaveState));

/**
 * Saves the page and resolves true when it is stored, so publishing can follow: the styles
 * first, on their own, then the page if it has changes (see createPageSaver).
 */
const save = createPageSaver({
  flushStyles: () => (editorOnly ? Promise.resolve(true) : flushStylesForSave()),
  pageDirty,
  // Declared right below; only called once somebody saves.
  savePage: () => exclusive(() => saveNow()),
});
/**
 * A save that met a newer version of the page asks what to do (#70): reload and lose the
 * changes, copy their HTML first, or, for the owner, save over the other version.
 */
const conflictDialog = initConflictDialog(query<HTMLDialogElement>("[data-conflict-dialog]"), {
  owner: !editorOnly,
  copyHtml: async () => {
    let html: string;
    try {
      html = await api.renderHtml(store.toJSON().root.children ?? []);
    } catch (error) {
      showError(error, "The HTML was not copied");
      return;
    }
    await copyText(html, { copied: "Copied the page's HTML", failed: "The HTML was not copied", failedDetail: "The browser refused the clipboard" });
  },
});
const resolveConflict = async (conflict: { saved: string | null }): Promise<void> => {
  const choice = await conflictDialog.ask(conflict);
  if (choice === "reload") {
    // The person chose to lose the changes; the browser must not ask again.
    leaving = true;
    location.reload();
  } else if (choice === "keep") {
    await exclusive(() => saveNow(true));
  }
};
const saveNow = async (force = false): Promise<boolean> => {
  saveButton.disabled = true;
  try {
    const sent = store.toJSON();
    const result = await api.savePage(session.pageId, {
      document: sent,
      pageRevision: session.pageRevision,
      siteRevision: session.siteRevision,
      stylesHash: session.stylesHash,
      ...(force ? { force } : {}),
    });
    session.pageRevision = result.pageRevision;
    session.siteRevision = result.siteRevision;
    session.stylesHash = result.stylesHash;
    // What was sent is stored now; changes made while the save was on its way are not.
    savedPage = pageFingerprint(sent);
    savedStyles = stylesFingerprint(sent);
    if (result.styles) {
      // Another page changed the site styles meanwhile and this page never touched its
      // copy: the core kept the newer ones, and the page shows them from now on. What the
      // core holds now is the new styles, so only a page that stayed as it was sent may
      // count as saved with them; anything typed meanwhile stays unsaved.
      const unchangedSince = !pageDirty();
      styles.replaceSiteStyles(result.styles);
      if (unchangedSince) savedStyles = stylesFingerprint(store.toJSON());
      showToast("Saved; the site styles were updated from another page", { kind: "success" });
    } else {
      showToast("Saved", { kind: "success" });
    }
    // Saving moved the page away from what visitors have: the page list says so from now
    // on, so it has to hear about the save.
    void pages.refresh();
    showSaveState();
    return true;
  } catch (error) {
    const conflict = pageConflict(error);
    if (conflict !== null) {
      void resolveConflict(conflict);
      return false;
    }
    if (error instanceof ApiRequestError) {
      const first = error.errors[0];
      showError(first && first.path && error.status === 400 ? `${first.message} (${first.path})` : error.message, "The page was not saved");
    } else {
      showError(error, "The page was not saved");
    }
    return false;
  } finally {
    saveButton.disabled = false;
  }
};
const publish = initPublish(query("[data-publish]", app), {
  save,
  // Declared further down; only called once somebody publishes. With nothing to save, the
  // waiting styles alone go first, without the message Save gives.
  saveOpen: () => (saveDirty() ? saveWhatIsOpen() : save()),
  // What a mode edits goes out alone, else the open page or template (#106).
  target: () => {
    const component = store.editedComponent;
    if (component) return { piece: { kind: "component", id: component.componentId }, noun: "component", name: component.name };
    const frame = store.editedFrame;
    if (frame) return { piece: { kind: "frame", id: frame.frameId }, noun: "frame", name: frame.name };
    const consent = store.editedConsent;
    if (consent) return { piece: { kind: "consent", id: consent.itemId }, noun: "element", name: consent.name };
    const page = pages.current();
    return page ? { piece: { kind: "page", id: page.id }, noun: page.template ? "template" : "page", name: page.name, draft: page.status === "draft" } : null;
  },
  // Waiting styles go live with the save before a publish; they make no piece "changed".
  dirty: saveOwnDirty,
  reloadOpen: () => {
    // What is open was written back as visitors have it; the builder loads it again.
    leaving = true;
    location.reload();
  },
  touchesOpen: (piece) => {
    switch (piece.kind) {
      case "page":
        return piece.id === session.pageId;
      case "frame":
        return store.frame === piece.id || store.editedFrame?.frameId === piece.id;
      case "component":
        return store.componentStanding(piece.id) !== "none" || store.componentShown(piece.id) !== null;
      case "consent":
        return store.editedConsent?.itemId === piece.id;
    }
    return false;
  },
  pages,
  session,
  exportDialog: query<HTMLDialogElement>("[data-export-dialog]"),
  checkDialog: query<HTMLDialogElement>("[data-publish-check]"),
  // Declared further down; only called once somebody answers the check.
  openEntry: (collectionId, entryId) => {
    content.openEntry(collectionId, entryId);
    manage.show("content");
  },
  openMaintenance: () => {
    pagesTabs?.show("settings");
    manage.show("pages");
  },
  // The switch between Draft and Live wrote the page's status: the open page takes the
  // revision the core gave it, so its next save is no conflict.
  adoptRevisions: (revisions) => {
    const step = revisions[session.pageId];
    if (step && step.before === session.pageRevision) session.pageRevision = step.after;
  },
  // A publish of everything takes the stored styles along, and the first makes a website.
  published: () => {
    websitePublished = true;
    void api
      .site()
      .then((summary) => {
        stylesWaiting = summary.stylesWaiting;
        showStylesState();
        showSaveState();
      })
      .catch(() => undefined);
  },
});
refreshPublish = publish.refresh;
// The Publishing section above the structure says where what is open stands, from the plan the
// taskbar read; its button is the taskbar's (#106).
{
  const section = query('[data-sidebar-section="publishing"]', app);
  const body = query("[data-sidebar-publishing]", section);
  const badge = query("[data-sidebar-publishing-badge]", section);
  publish.onPlan((target, plan) => {
    section.hidden = target === null;
    if (!target) return;
    const page = target.piece.kind === "page" ? pages.current() : null;
    // The card is drawn anew with every plan; a switch that had the focus keeps it.
    const focused = badge.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset["visibility"] : undefined;
    const card = createPublishCard({
      target,
      draft: page?.status === "draft",
      facts: page ? { created: page.created, saved: page.saved } : {},
      badgeSlot: badge,
      plan,
      // The switch between Draft and Live publishes the page at once, saving it first
      // (decided 2026-11-03); a role every visitor meets keeps it on the website.
      ...(page ? { toggle: { draftOff: () => draftRefusal(roleOf(page)) } } : {}),
      ...(editorOnly ? {} : { actions: { run: publish.runOpen, schedule: true, dirty: saveOwnDirty } }),
    });
    body.replaceChildren(card.element);
    if (focused) badge.querySelector<HTMLElement>(`[data-visibility="${focused}"]`)?.focus();
  });
}

/** Counts the other pages' use of the styles again; the style manager sets it, if there is one. */
let refreshStyleUsage = (): void => undefined;
/** The canvas's light and dark toggle, made with the canvas below; beanCSS tells it its settings. */
let canvasScheme: CanvasScheme | null = null;

// A class rename reaches the other pages of the site through the core. Undo takes it
// back on this page only; the other pages keep the new name.
styles.onRenameClass = (from, to) => {
  void api
    .renameClass(from, to)
    .then((result) => {
      session.siteRevision = result.siteRevision;
      session.stylesHash = result.stylesHash;
      store.adoptComponentRevisions(result.components ?? {});
      // The core rewrote the stored page too; this page holds the rename already.
      const step = result.revisions?.[session.pageId];
      if (step && step.before === session.pageRevision) session.pageRevision = step.after;
      refreshStyleUsage();
      const others = Object.keys(result.pages).filter((id) => id !== session.pageId).length;
      if (others > 0) showToast(`Renamed .${from} to .${to} on ${others === 1 ? "1 other page" : `${others} other pages`} too`, { kind: "success" });
    })
    .catch((error: unknown) => {
      showToast("Renamed on this page only", { kind: "warning", detail: error });
    });
};
if (componentsDialog) {
  componentMode = initComponentMode({
    store,
    styles,
    dialog: componentsDialog,
    session,
    elements: {
      save: saveButton,
      exit: query<HTMLButtonElement>("[data-component-exit]", app),
      status: query("[data-component-status]", app),
      locked: [],
      switcher: query("[data-pages]", app),
    },
    savedStyles: () => savedStyles,
    stylesSaved: (fingerprint) => {
      savedStyles = fingerprint;
    },
    exclusive,
    flushStyles: flushStylesForSave,
    onSaved: () => {
      void pages.refresh();
      showSaveState();
    },
    // Declared right below; while it is null there is no frame mode to put back.
    onModeChange: () => {
      frameMode?.refresh();
      showSaveState();
    },
  });
  frameMode = initFrameMode({
    store,
    styles,
    session,
    elements: {
      save: saveButton,
      exit: query<HTMLButtonElement>("[data-frame-exit]", app),
      status: query("[data-frame-status]", app),
      // The page switcher stays: it names the frame and switches to another or back.
      locked: [],
      chip: query("[data-frame-chip]", app),
      chipName: query("[data-frame-chip-name]", app),
      chipEdit: query<HTMLButtonElement>("[data-frame-chip-edit]", app),
      preview: query<HTMLButtonElement>("[data-frame-preview]", app),
      previewName: query("[data-frame-preview-name]", app),
      previewBadges: query("[data-frame-preview-badges]", app),
      previewList: query("[data-frame-preview-menu]", app),
      previewField: query("[data-frame-preview-field]", app),
    },
    pageId: session.pageId,
    // Templates too: one other than the open page shows its first entry.
    pagesIn: (frameId) => pages.list().filter((page) => page.frame === frameId),
    savedStyles: () => savedStyles,
    stylesSaved: (fingerprint) => {
      savedStyles = fingerprint;
    },
    exclusive,
    flushStyles: flushStylesForSave,
    onSaved: () => {
      void pages.refresh();
      showSaveState();
    },
    leaveComponent: () => componentMode?.leave() ?? Promise.resolve(true),
    onModeChange: () => {
      showSaveState();
      pages.redraw();
      entryChip.setAway(store.editedFrame !== null && store.framePreviewChoice.kind !== "own");
    },
  });
  consentMode = initConsentMode({
    store,
    styles,
    session,
    pageId: session.pageId,
    pages: () => pages.list(),
    elements: {
      save: saveButton,
      exit: query<HTMLButtonElement>("[data-consent-exit]", app),
      status: query("[data-consent-status]", app),
      // The page is not what the element is about: its switcher, chips and publish go meanwhile.
      locked: [query("[data-pages]", app), query("[data-entry-chip]", app)],
      bar: query("[data-layer-bar]", app),
      empty: query("[data-consent-empty]", app),
      itemField: query("[data-consent-item-field]", app),
      item: query<HTMLButtonElement>("[data-consent-item]", app),
      itemName: query("[data-consent-item-name]", app),
      itemBadges: query("[data-consent-item-badges]", app),
      itemList: query("[data-consent-item-menu]", app),
      backgroundField: query("[data-consent-background-field]", app),
      background: query<HTMLButtonElement>("[data-consent-background]", app),
      backgroundName: query("[data-consent-background-name]", app),
      backgroundBadges: query("[data-consent-background-badges]", app),
      backgroundList: query("[data-consent-background-menu]", app),
      widgets: consentWidgetsOpener,
    },
    savedStyles: () => savedStyles,
    stylesSaved: (fingerprint) => {
      savedStyles = fingerprint;
    },
    exclusive,
    flushStyles: flushStylesForSave,
    leaveOthers: async () => {
      if (componentMode?.active && !(await componentMode.leave())) return false;
      return frameMode?.active ? frameMode.leave() : true;
    },
    onModeChange: () => {
      frameMode?.refresh();
      showSaveState();
      // The page switcher lists the consent elements, whose standing a save changes.
      void pages.refresh();
    },
  });
}

/** Opens an element of the cookie consent in the consent mode, leaving one edited first. */
async function editConsent(itemId: string): Promise<void> {
  if (store.editedConsent?.itemId === itemId) return;
  if (consentMode?.active && !(await consentMode.leave())) return;
  await consentMode?.enter(itemId);
}

/**
 * Builds the preview and opens it in a tab of its own, after saving what waits, as Preview
 * does; to try the consent banner, with it on every page and shown on arrival.
 */
async function previewInTab(consent = false, consentItem = ""): Promise<void> {
  const tab = openTab();
  try {
    if (!(await saveEverything())) {
      tab?.close();
      return;
    }
    const result = await api.previewSite(session.pageId, session.collection, session.entry, consent, consentItem);
    if (tab) tab.location.href = result.url;
    else showToast("Allow pop-ups for this site to open the preview", { kind: "warning" });
  } catch (error) {
    tab?.close();
    showError(error, "The preview could not be built");
  }
}

/**
 * Enters a component from the components list where it stands in what is open, the page
 * or the frame being edited: its first place, selected so the tree and the canvas show
 * where it is. Not inside another component, which the person leaves first.
 */
function editComponentHere(componentId: string): boolean {
  if (store.editedComponent?.componentId === componentId) return true;
  const place = store.placeOf(componentId);
  if (!componentMode || place === null) return false;
  if (store.editedComponent !== null) {
    showToast("Leave the component you are editing first", { kind: "warning" });
    return false;
  }
  store.select(place);
  componentMode.commands.edit(place);
  return true;
}

// Edit in the components list on another page: that page opened for it, so it starts there.
const handedComponent = takeHandedComponent(session.pageId);
if (handedComponent !== null) editComponentHere(handedComponent);

/**
 * Edits a frame around the open page, any frame, one no page uses yet too; the one being
 * edited is left first, asking about its changes.
 */
async function editFrame(frame: FrameSummary): Promise<void> {
  if (!frameMode || store.editedFrame?.frameId === frame.id) return;
  if (frameMode.active && !(await frameMode.leave())) return;
  store.setFrameDefinition(frame.id, { name: frame.name, revision: frame.revision, root: frame.root, ids: frame.ids, scripts: frame.scripts });
  frameMode.edit(frame.id);
}
/** Before leaving the page: the styles, the component being edited, then the page if it changed. */
const saveEverything = async (): Promise<boolean> => {
  if (!editorOnly && !(await flushStylesForSave())) return false;
  if (componentMode?.active && componentMode.isDirty() && !(await componentMode.save())) return false;
  if (frameMode?.active && frameMode.isDirty() && !(await frameMode.save())) return false;
  if (consentMode?.active && consentMode.isDirty() && !(await consentMode.save())) return false;
  return pageDirty() ? save() : true;
};
/**
 * Save: the component's while one is edited, else the page's, and the styles go live with it
 * where they wait (decided 2026-11-03), also when nothing else changed.
 */
const saveWhatIsOpen = async (): Promise<boolean> => {
  const stylesWait = stylesToGo();
  const own = saveOwnDirty();
  let ok: boolean;
  if (componentMode?.active) ok = own ? await componentMode.save() : true;
  else if (frameMode?.active) ok = own ? await frameMode.save() : true;
  else if (consentMode?.active) ok = own ? await consentMode.save() : true;
  else {
    // A page without changes is not sent again; Save and Ctrl+S still say that all is stored.
    ok = await save();
    if (ok && !own && !stylesWait) showToast("Everything is saved", { kind: "success" });
  }
  if (!ok || !stylesWait) return ok;
  return stylesGoLive(own ? "The styles went live with it" : "The styles are live");
};
saveButton.addEventListener("click", () => {
  if (refused(saveButton)) return;
  void saveWhatIsOpen();
});
showSaveState();
// ---- The manage panel ----

/**
 * Everything about the website that is not the page in the canvas, in one panel with a nav
 * of its own, grouped into categories. Which sections are built is what the role may do: an
 * editor fills the content, moderates comments, uploads files and logs out again, the owner
 * has the whole website.
 */
const manageRoot = query("[data-manage]", app);
const manageElements = { root: manageRoot, app };
const sectionRoot = (id: string) => query(`[data-manage-section="${id}"]`, manageRoot);

/**
 * A section with the tab Settings beside its own (decided 2026-10-30, proposal H of "Settings
 * Where They Act"): `load` reads what Settings shows, when the tab is picked and whenever the
 * section opens on it; `kept` receives the tabs, for a link that leads to one of them.
 */
const withSettingsTab = <S extends { show?: () => void }>(id: string, section: S, load: () => void, kept: (tabs: SectionTabs) => void = () => undefined): S => {
  const tabs = initSectionTabs(sectionRoot(id), id, (tab) => {
    if (tab === "settings") load();
  });
  kept(tabs);
  return {
    ...section,
    show: () => {
      section.show?.();
      if (tabs.current() === "settings") load();
    },
  };
};

const content = initContentPanel(sectionRoot("content"), {
  mayChangeSchema: !editorOnly,
  onReferencesMoved: followMovedReferences,
  columnsDialog: editorOnly ? null : query<HTMLDialogElement>("[data-columns-dialog]"),
  mayPreview: !editorOnly,
  imagePicker,
  ...(editorOnly ? {} : { publish: publish.act, publishMany: publish.many, takeOff: publish.takeOff, unpublish: publish.unpublish }),
});

const groups: ManageGroup[] = [];
if (!editorOnly) {
  groups.push({
    id: "website",
    label: "Website",
    icon: "globe",
    color: "blue",
    sections: [
      {
        id: "pages",
        label: "Pages",
        icon: "page",
        intro: "Every page of the website, at its own address: edit one in the canvas, or change its address, frame and visibility in its settings",
        ...(() => {
          pagesSection = initPagesSection(sectionRoot("pages"), pages, "pages", { close: () => manage.setOpen(false), ...(editorOnly ? {} : { publish: publish.many, takeOff: publish.takeOff, unpublish: publish.unpublish }) });
          // Maintenance mode in Settings, where its page is (under Website → Settings before 2026-10-30).
          const maintenance = initMaintenanceCard(query("[data-maintenance]", sectionRoot("pages")), {
            openPage: (page) => (page ? pages.openSettings(page) : pages.openNew("maintenance")),
            siteUrl: () => pages.site()?.url ?? "",
          });
          const load = () =>
            void api.site().then(
              (info) => maintenance.load(info.site.maintenance, info.pages),
              (error: unknown) => showError(error, "Maintenance mode could not be read"),
            );
          return withSettingsTab("pages", pagesSection, load, (tabs) => (pagesTabs = tabs));
        })(),
      },
      {
        id: "routes",
        label: "Routes",
        icon: "routes",
        intro: "Every address of the website and what it shows; a page sets its address in its settings, a collection the address of its entries in its fields",
        ...(() => {
          // The notice to search engines in Settings, beside the sitemap (under Website → Settings before 2026-10-30).
          const notices = initNoticeCard(query("[data-indexnow]", sectionRoot("routes")));
          let routesTabs: SectionTabs | null = null;
          const routes = initRoutesSection(sectionRoot("routes"), {
            pages,
            close: () => manage.setOpen(false),
            openCollection: (id) => {
              content.openFields(id);
              manage.show("content");
            },
            openEntry: (collectionId, entryId) => {
              content.openEntry(collectionId, entryId);
              manage.show("content");
            },
            openRedirects: () => manage.show("redirects"),
            openNotices: () => routesTabs?.show("settings"),
          });
          return withSettingsTab("routes", routes, () => notices.load(), (tabs) => (routesTabs = tabs));
        })(),
      },
      {
        id: "redirects",
        label: "Redirects",
        icon: "redirect",
        intro: "Old addresses and where they lead now, so links from elsewhere keep working after a page, an entry or a collection moved",
        ...initRedirectsSection(sectionRoot("redirects")),
      },
      {
        id: "settings",
        label: "Settings",
        icon: "gear",
        intro: "The name, the language of every published page, the domains the website and its preview answer on, and the security headers it sends",
        ...(websiteSection = initWebsiteSection(sectionRoot("settings"))),
      },
      {
        id: "consent",
        label: "Consent",
        icon: "cookie",
        intro: "What the website asks its visitors before it loads videos, maps and other providers' services, and the proof of what they chose",
        ...initConsentSection(sectionRoot("consent"), {
          edit: (itemId) => {
            manage.setOpen(false);
            void editConsent(itemId);
          },
          editing: () => store.editedConsent?.itemId ?? null,
          renamed: (itemId, name) => {
            if (store.editedConsent?.itemId === itemId) store.renameConsent(name);
          },
          preview: (itemId) => void previewInTab(false, itemId),
          openLog: () => manage.show("consents"),
        }),
      },
    ],
  });

  // What the pages are built from, beside what visitors find: the frame around them, the
  // templates of entries, the pieces placed on many pages, and the styles.
  const styleManager = initStyleManager(sectionRoot("styles"), store, styles, {
    close: () => manage.setOpen(false),
    loadUsage: async () => usageElsewhere(await api.classUsage(), session.pageId),
  });
  refreshStyleUsage = styleManager.refreshUsage;
  groups.push({
    id: "design",
    label: "Design",
    icon: "palette",
    color: "violet",
    sections: [
      {
        id: "frames",
        label: "Frames",
        icon: "frame",
        intro: "What surrounds the pages: the header, the footer and the rest every page repeats, around each page's content; pages and templates pick theirs in their settings",
        ...(framesSection = initFramesSection(sectionRoot("frames"), query<HTMLDialogElement>("[data-frame-name-dialog]"), query<HTMLDialogElement>("[data-frame-delete-dialog]"), {
          pageFrame: () => store.frame,
          saveFirst: () => saveEverything(),
          reloadPage: () => {
            leaving = true;
            location.reload();
          },
          onChange: () => void pages.refresh(),
          // Any frame opens in the frame mode on the open page, a frame no page uses yet too.
          editFrame: (frame) => {
            manage.setOpen(false);
            void editFrame(frame);
          },
          publish: publish.act,
        })),
      },
      {
        id: "templates",
        label: "Templates",
        icon: "grid",
        intro: "The pages of entries: a collection shows each of its entries with a template, at the address it sets",
        ...(templatesSection = initPagesSection(sectionRoot("templates"), pages, "templates", { close: () => manage.setOpen(false), publish: publish.many, takeOff: publish.takeOff, unpublish: publish.unpublish })),
      },
      {
        id: "components",
        label: "Components",
        icon: "component",
        intro: "Elements built once and placed on many pages; Edit opens one where it stands, on a page or in a frame",
        ...initComponentsSection(sectionRoot("components"), store, session.pageId, {
          edit: (componentId, target) => {
            if (target.kind === "here") {
              if (editComponentHere(componentId)) manage.setOpen(false);
            } else if (target.kind === "page") {
              handOverComponent(target.pageId, componentId);
              void pages.goTo(target.pageId);
            } else if (target.kind === "frame") {
              // The frame mode on the open page, then the component inside the frame.
              void api
                .frames()
                .then(async (frames) => {
                  const frame = frames.find((candidate) => candidate.id === target.frameId);
                  if (!frame) return;
                  manage.setOpen(false);
                  if (store.editedFrame?.frameId !== frame.id) await editFrame(frame);
                  if (store.editedFrame?.frameId === frame.id) editComponentHere(componentId);
                })
                .catch((error: unknown) => showError(error));
            }
          },
          publish: publish.act,
        }),
      },
      {
        id: "fonts",
        label: "Fonts",
        icon: "font",
        intro: "The typefaces of the website, which one is for headings, text and code, and the CSS every page carries of them",
        ...initFontsSection(sectionRoot("fonts")),
      },
      {
        id: "beancss",
        label: "beanCSS",
        icon: "bean",
        intro: "The framework of the website: colours with their steps, roles for light and dark, and fluid sizes for text and headings, as variables your classes read",
        ...initBeanSection(sectionRoot("beancss"), {
          hasStylesheet: (name) => styles.getStylesheet(name) !== undefined,
          keepStylesheet: async (name, css) => {
            if (!styles.getStylesheet(name)) styles.addStylesheet(name);
            styles.setStylesheetCss(name, css);
            return stylesAutosave.flush();
          },
          dropStylesheet: async (name) => {
            styles.removeStylesheet(name);
            return stylesAutosave.flush();
          },
          onSettings: (settings) => canvasScheme?.set(settings),
        }),
      },
      {
        id: "styles",
        label: "Styles",
        icon: "drop",
        intro: "The classes, ids and stylesheets of the website, and the variables they read",
        ...styleManager,
      },
    ],
  });
}

const manageOpener = query<HTMLButtonElement>("[data-manage-open]", app);
const manageBadge = query("[data-manage-badge]", app);
/** What waits for somebody: comments to approve and messages of the forms nobody opened. */
const waitingNow = { comments: 0, messages: 0 };
// The taskbar's opener says it too, so what waits is seen without opening the panel.
const showWaiting = () => {
  const waiting = waitingComments(waitingNow.comments, waitingNow.messages);
  manageBadge.textContent = waiting.badge;
  manageBadge.hidden = waiting.badge === "";
  manageOpener.setAttribute("aria-label", waiting.label);
  setTip(manageOpener, waiting.label);
};
const comments = initCommentsSection(sectionRoot("comments"), {
  onPending: (count) => {
    manage.setBadge("comments", count, commentsWaiting(count));
    waitingNow.comments = count;
    showWaiting();
  },
});
const forms = initFormsSection(sectionRoot("forms"), {
  maySetUp: !editorOnly,
  onNew: (count) => {
    manage.setBadge("forms", count, messagesWaiting(count));
    waitingNow.messages = count;
    showWaiting();
  },
});
groups.push({
  id: "content",
  label: "Content",
  icon: "layers",
  color: "green",
  sections: [
    {
      id: "content",
      label: "Collections",
      icon: "database",
      intro: "Collections hold the entries pages repeat; with a template, every entry also gets a page of its own at the address the collection sets",
      ...content,
    },
    {
      id: "forms",
      label: "Forms",
      icon: "form",
      intro: "What visitors send with the website's forms: kept for as long as each form says, read here, never shown on the website",
      ...forms,
    },
    {
      id: "comments",
      label: "Comments",
      icon: "comment-list",
      intro: "What visitors left: nothing shows on the website until you approve it, and approving takes effect without publishing again",
      ...comments,
    },
    {
      id: "uploads",
      label: "Uploads",
      icon: "upload",
      intro: "Images, fonts and files of the website; pages refer to one as /assets/name, and a publish takes along only the ones a page uses",
      ...initAssetsPanel(sectionRoot("uploads"), store, { close: () => manage.setOpen(false), lightbox, renamed: followRenamedUpload, settings: !editorOnly, imagePicker }),
    },
    // The proof of the visitors' choices about cookies is the owner's, as their settings are.
    ...(editorOnly
      ? []
      : [
          {
            id: "consents",
            label: "Consents",
            icon: "consent-declaration" as const,
            intro: "Every choice visitors made about cookies, with the banner they saw: the proof the GDPR asks for",
            ...initConsentsSection(sectionRoot("consents"), { openSettings: () => manage.show("consent") }),
          },
        ]),
  ],
});

if (!editorOnly) {
  // Two lists, not one: an account opens the builder and lives in app.db, a member only
  // reads the pages marked members only and lives with the website. Removing one is not
  // the same thing as removing the other, so they never share a list.
  groups.push({
    id: "people",
    label: "People",
    icon: "users",
    color: "orange",
    sections: [
      {
        id: "people",
        label: "Editors",
        icon: "user-edit",
        intro: "Who may edit this website: an editor maintains texts, pictures, links and entries; the owner has everything",
        ...initPeople(sectionRoot("people"), account.id),
      },
      {
        id: "members",
        label: "Members",
        icon: "users",
        intro: "People who may read the pages marked members only; they are not accounts of the builder and cannot open it",
        ...withSettingsTab(
          "members",
          initMembers(sectionRoot("members"), {
            openLoginPage: (page) => (page ? pages.openSettings(page) : pages.openNew("member-login")),
          }),
          // The members section reads who may sign up whenever it opens.
          () => undefined,
        ),
      },
    ],
  });
}

/** The builder itself rather than the website: how it looks, and who is looking. */
const builderSections: ManageSection[] = [];
if (!editorOnly) {
  builderSections.push(
    {
      id: "address",
      label: "Address",
      icon: "link",
      intro: "Where the builder itself answers, kept with the accounts on this server, not with the website",
      ...initAddressSection(sectionRoot("address")),
    },
    {
      id: "preferences",
      label: "Preferences",
      icon: "settings",
      intro: "How the builder behaves for you, kept in this browser, not on the website",
      ...initPreferencesSection(sectionRoot("preferences"), settings),
    },
  );
}
builderSections.push({
  id: "account",
  label: "Account",
  icon: "user",
  intro: "The account you are logged in with",
  ...initAccountSection(sectionRoot("account"), {
    mayLeave: () => pages.mayLeave(),
    leaving: () => {
      leaving = true;
    },
  }),
});
// The machine is the operator's console, not a section: a link in a new tab, shown only
// when the operator told the website binary where it answers.
groups.push({
  id: "builder",
  label: "Builder",
  icon: "box",
  color: "yellow",
  sections: builderSections,
  links: editorOnly ? [] : [{ id: "machine", label: "elpresso machine", icon: "gauge", href: "" }],
});

const manage = initManage(manageElements, groups, {
  // Entries, uploads and pages may have changed while the panel was open; the canvas follows.
  onClose: () => {
    // Every section saves itself, the style manager too: what waits for its pause goes now.
    if (!editorOnly) void stylesAutosave.flush();
    canvas.refresh();
    entryChip.reload();
    element.contentChanged();
    // A collection may have picked this page as its template, or dropped it.
    void pages.refresh();
  },
});
// While maintenance mode is on, the panel's header says so in every section, as the taskbar
// does under it, and leads to the maintenance page's row, where the switch is (2026-10-23).
const manageAway = query<HTMLButtonElement>("[data-manage-away]", manageRoot);
manageAway.addEventListener("click", () => {
  const page = pages.list().find((candidate) => roleOf(candidate) === "maintenance");
  if (page) pagesSection?.openPage(page.id);
  pagesTabs?.show("pages");
  manage.show("pages");
});
onMaintenanceChange((on) => {
  publish.setAway(on);
  manageAway.hidden = editorOnly || !on;
});
// Until the website has its domain the server answers on its defaults: the owner is taken
// to the settings once, where the setup notice sits, and may close it and come back.
if (!editorOnly) {
  void api.site().then(
    (info) => {
      manage.setLink("machine", info.site.hosts.machine);
      manageAway.hidden = !info.site.maintenance;
      if (!info.site.configured) {
        websiteSection?.general();
        manage.show("settings");
      }
    },
    // The link and the setup notice can wait for the next start; the builder works without.
    () => undefined,
  );
}
// Ctrl+S saves the page, so it waits while the manage panel is open: nothing in there is
// saved by it, and the page it would save is not the one on the screen.
window.addEventListener("keydown", (event) => {
  if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "s") {
    event.preventDefault();
    if (!manage.open) void saveWhatIsOpen();
  }
});

manage.addOpener(manageOpener);
manage.addOpener(query("[data-style-manager-open]", app), "styles");
// The badge is right before the panel was ever opened, and stays right while the builder
// is open: asked again every few minutes and on coming back to the tab, but not while the
// panel is open, where a reload of the list would pull it away under the pointer.
comments.refresh();
forms.refresh();
const askComments = () => {
  if (manage.open || document.visibilityState !== "visible") return;
  comments.refresh();
  forms.refresh();
};
window.setInterval(askComments, COMMENTS_EVERY);
document.addEventListener("visibilitychange", askComments);

const canvas = initCanvas(query("[data-canvas-viewport]", app), store, styles, hidden, {
  history,
  structure,
  element,
  session,
  frameClicked: () => {
    const name = store.frameDefinition(store.frame)?.name ?? "the frame";
    showToast(frameMode ? `This belongs to the frame ${name}, whose pencil above the canvas edits it for every page in it` : `This belongs to the frame ${name}`, { kind: "info" });
  },
});
// The canvas shows the page in light or dark while beanCSS writes both; an editor cannot read
// beanCSS's settings and gets no toggle.
canvasScheme = initCanvasScheme(query<HTMLButtonElement>("[data-canvas-scheme]", app), canvas, () => setsColorScheme(styles.allCss));
// First of the canvas's view toggles: the lists show their drafts marked, or leave them out
// as visitors get the page after the next publish; view state, never saved.
const draftsButton = query<HTMLButtonElement>("[data-canvas-drafts]", app);
let hideDrafts = false;
draftsButton.append(createIcon("drafts", "icon-button__icon"));
const drawDrafts = () => {
  draftsButton.setAttribute("aria-pressed", String(hideDrafts));
  setLabel(draftsButton, hideDrafts ? "Show the drafts in the lists again" : "Show the page without drafts, as visitors get it after the next publish");
};
draftsButton.addEventListener("click", () => {
  hideDrafts = !hideDrafts;
  drawDrafts();
  canvas.setHideDrafts(hideDrafts);
});
drawDrafts();
if (!editorOnly) {
  // Beside the moon: the page as a visitor who did not consent gets it, its embeds behind
  // their placeholder (#92); view state, never saved.
  const visitorButton = query<HTMLButtonElement>("[data-canvas-visitor]", app);
  let visitor = false;
  visitorButton.append(createIcon("cookie", "icon-button__icon"));
  const drawVisitor = () => {
    visitorButton.setAttribute("aria-pressed", String(visitor));
    setLabel(visitorButton, visitor ? "Show the page as you edit it" : "Show the page as a visitor who did not consent");
  };
  visitorButton.addEventListener("click", () => {
    visitor = !visitor;
    drawVisitor();
    canvas.setVisitor(visitor);
  });
  drawVisitor();
  visitorButton.hidden = false;
}
styles.onChange(() => canvasScheme?.stylesChanged());
if (editorOnly) canvasScheme.set(null);
else void api.bean().then((state) => canvasScheme?.set(state.settings)).catch(() => canvasScheme?.set(null));


const entryChip = initEntryChip(query("[data-entry-chip]", app), session, {
  onChange: () => canvas.refresh(),
  openEntries: () => manage.show("content"),
});
// The page settings and the collections' detail views decide which collections a template
// serves; the chip and the canvas follow when that changed.
let chipServes = JSON.stringify(session.serves);
pages.onChange(() => {
  const page = pages.current();
  if (page) {
    session.serves = page.collections;
    const collection = templateCollection(page.collections, session.collection);
    if (collection !== session.collection) {
      session.collection = collection;
      session.entry = "";
      canvas.refresh();
    }
  }
  const serves = JSON.stringify(session.serves);
  if (serves === chipServes) return;
  chipServes = serves;
  entryChip.reload();
});

